import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    Input,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { AppTextService } from '../app-text';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';

export enum PasswordInputType {
    Text = 'text',
    Password = 'password',
}

@Component({
    selector: 'fi-user-set-password',
    templateUrl: './set-password.component.html',
    styleUrl: './set-password.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-set-password' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiUserSetPasswordComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiUserSetPasswordComponent,
        },
    ],
    standalone: false,
})
export class FiUserSetPasswordComponent<
    T extends { Id: number; Password: string; ConfirmPassword: string },
> extends ControlValueAccessorValidatorBase<T> {
    @Input() isShowPasswordButtonShown: boolean;
    @Input() isRequired: boolean;

    @ViewChild('passwordInputRef', { read: ElementRef, static: false }) passwordInputRef: ElementRef;
    @ViewChild('confirmPasswordInputRef', { read: ElementRef, static: false }) confirmPasswordInputRef: ElementRef;
    @ViewChild('PasswordModel', { static: false }) PasswordModel: NgModel;
    @ViewChild('ConfirmPasswordModel', { static: false }) ConfirmPasswordModel: NgModel;

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
    ) {
        super(injector);
    }

    getPasswordInputLabel(): string {
        return this.appTextService.get(this.ngModel.Id ? 'common.new_password' : 'common.password');
    }

    getRepeatPasswordInputLabel(): string {
        return this.appTextService.get(this.ngModel.Id ? 'common.repeat_new_password' : 'common.repeat_password');
    }

    getPasswordPlaceholder(): string {
        return this.ngModel.Id === 0 ? this.appTextService.get('common.required') : '******';
    }

    togglePasswordVisibility(passwordInputRef: HTMLInputElement) {
        passwordInputRef.type =
            passwordInputRef.type === PasswordInputType.Password ? PasswordInputType.Text : PasswordInputType.Password;
    }

    getPasswordSuffixIconClassName(passwordInputRef: HTMLInputElement): { [key: string]: boolean } {
        return {
            [this._passwordInputTypeToSuffixIconMap[passwordInputRef.type]]: true,
        };
    }

    private readonly _passwordInputTypeToSuffixIconMap: Readonly<{ [key: string]: string }> = {
        [PasswordInputType.Password]: 'fa-eye',
        [PasswordInputType.Text]: 'fa-eye-slash',
    };

    readonly passwordInputErrorMessages: Readonly<{ [key: string]: string }> = {
        minlength: 'error_message.password_not_approved',
        passwordStrength: 'error_message.password_not_approved',
    };

    readonly confirmPasswordInputErrorMessages: Readonly<{ [key: string]: string }> = {
        equalTo: 'error_message.password_not_approved',
    };
}
